import http from "./http"
import { baseApi } from "../config"
import { getStoredToken } from "./http"

/**
 * Uploading and fetching files.
 *
 * ── Why downloads do not just use an <img src> or an <a href> ───────────────
 *
 * The download route is authenticated with a Bearer token, and a browser does
 * not attach one to an `<img>` or a plain link. Putting the token in the URL
 * instead would leak it into browser history, the Referer header and any log
 * the URL touches.
 *
 * So a file is fetched with the normal authenticated client and turned into a
 * blob URL. The cost is that blob URLs must be revoked, which `useObjectUrl`
 * below handles.
 */

/** @returns {string} the API path for a file, for use with `fetchObjectUrl`. */
export function fileUrl(clientId, fileId, { download = false } = {}) {
  return `/uploads/${clientId}/${fileId}${download ? "?download=1" : ""}`
}

export const uploadApi = {
  /**
   * Send files to a chat thread.
   *
   * `onProgress` gets 0–100. Progress is worth wiring up here specifically: a
   * 20MB statement on a slow connection is thirty seconds of a composer that
   * otherwise looks frozen.
   */
  toChatRoom: (roomId, files, { body, onProgress } = {}) => {
    const form = new FormData()
    for (const file of files) form.append("files", file)
    if (body) form.append("body", body)

    return http
      .post(`/chat/rooms/${roomId}/attachments`, form, {
        // The Content-Type is stripped for FormData bodies in the request
        // interceptor, so the browser supplies the multipart boundary.
        onUploadProgress: (event) => {
          if (!onProgress || !event.total) return
          onProgress(Math.round((event.loaded * 100) / event.total))
        },
      })
      .then((r) => r.data)
  },

  toTask: (taskId, files, { onProgress } = {}) => {
    const form = new FormData()
    for (const file of files) form.append("files", file)

    return http
      .post(`/tasks/${taskId}/attachments`, form, {
        onUploadProgress: (event) => {
          if (!onProgress || !event.total) return
          onProgress(Math.round((event.loaded * 100) / event.total))
        },
      })
      .then((r) => r.data)
  },

  removeTaskAttachment: (taskId, attachmentId) =>
    http.delete(`/tasks/${taskId}/attachments/${attachmentId}`).then((r) => r.data),

  meta: (clientId, fileId) => http.get(`${fileUrl(clientId, fileId)}/meta`).then((r) => r.data),
}

/**
 * Fetch a file as a blob URL, with the auth header attached.
 *
 * The caller must revoke the returned URL — see `useObjectUrl`. Each one holds
 * the whole file in memory until it is revoked, so a chat scrolled through a few
 * hundred images would otherwise never give the memory back.
 *
 * @returns {Promise<string>} an object URL
 */
export async function fetchObjectUrl(path) {
  const response = await fetch(`${baseApi}${path}`, {
    headers: { Authorization: `Bearer ${getStoredToken()}` },
  })
  if (!response.ok) {
    /*
     * Three different things, said three different ways.
     *
     * 404 is the server's single answer for "no such file" and "not yours" —
     * deliberately, so sequential ids cannot be walked to map what exists. 502
     * means the row is there and the caller may have it, but the storage behind
     * it could not be read, which is worth retrying and worth saying so. Telling
     * somebody their file is gone when the bucket is merely unreachable sends
     * them looking for the wrong thing.
     */
    const error = new Error(
      response.status === 404
        ? "This file is no longer available."
        : response.status === 502
          ? "This file could not be loaded right now."
          : "Could not load the file.",
    )
    error.status = response.status
    error.retryable = response.status !== 404
    throw error
  }
  return URL.createObjectURL(await response.blob())
}

/**
 * Download a file to disk.
 *
 * Same fetch-then-blob dance, plus a synthetic click. The object URL is revoked
 * on a timeout rather than immediately: revoking it in the same tick cancels the
 * download in some browsers before it has started reading.
 */
export async function downloadFile(clientId, fileId, fileName) {
  const url = await fetchObjectUrl(fileUrl(clientId, fileId, { download: true }))
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = fileName || "download"
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(url), 30000)
}

/** An icon name for a file type, for the attachment chips. */
export function fileKind(mimeType, fileName = "") {
  const type = String(mimeType || "")
  const ext = fileName.toLowerCase().split(".").pop()

  if (type.startsWith("image/")) return "image"
  if (type === "application/pdf" || ext === "pdf") return "pdf"
  if (["xlsx", "xls", "xlsm", "csv"].includes(ext)) return "sheet"
  if (["doc", "docx", "rtf"].includes(ext)) return "doc"
  if (["zip", "gz"].includes(ext)) return "archive"
  return "file"
}

/** Human-readable size. The server sends `size_label`; this covers local files
 *  that have not been uploaded yet. */
export function formatBytes(bytes) {
  const n = Number(bytes) || 0
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}
